import React from 'react';
import { cn } from '../lib/utils';

/**
 * Composant <RoadSign /> — fiche d'identité visuelle des panneaux français.
 *
 * Note (mise à jour 2026-05-04) :
 * Les pictogrammes SVG détaillés ont été supprimés car porteurs d'erreurs
 * visuelles fines (proportions, attitudes, motifs). On conserve toutefois :
 *   • la FORME officielle du panneau (triangle, disque, octogone, losange, carré)
 *   • la COULEUR officielle (rouge danger, bleu obligation/indication…)
 *   • le CODE officiel (ex : A1a, B14_50, AB4)
 *   • la DESCRIPTION précise (label texte)
 *   • la CATÉGORIE explicite (danger / interdiction / obligation / indication
 *     / intersection-priorité / feu)
 *
 * Cette approche garantit zéro erreur visuelle tout en préservant la valeur
 * pédagogique : forme + couleur + libellé = l'élève identifie sans
 * ambiguïté le type de panneau (la priorité ECSR 2026).
 *
 * L'API publique du composant (`code`, `size`, `label`, `className`) est
 * inchangée — toutes les pages appelantes (Revision, ExamenETM, ARevoir,
 * Lexique, PanneauxGallery, admin/Questions, etc.) continuent à
 * fonctionner sans modification.
 */

// ============================================================
// CHARTE FRANÇAISE OFFICIELLE (arrêté du 24 nov. 1967, IISR 2024)
// ============================================================
//   Triangle pointe haute, fond blanc, bordure rouge   → DANGER
//   Triangle pointe basse, fond blanc, bordure rouge   → CÉDEZ LE PASSAGE
//   Octogone rouge, texte blanc                          → STOP
//   Losange jaune sur blanc                              → ROUTE PRIORITAIRE
//   Disque blanc, bordure rouge                          → INTERDICTION
//   Disque bleu plein                                    → OBLIGATION
//   Carré bleu plein                                     → INDICATION
//   Rectangle vertical noir, 3 lampes                    → FEU TRICOLORE

const CAT_META = {
  danger: {
    label: 'DANGER',
    accent: '#cf2031',
    accentSoft: 'rgba(207,32,49,0.10)',
    description: 'Triangle blanc bordé de rouge, pointe vers le haut. Annonce un risque sur la route.',
  },
  intersection: {
    label: 'PRIORITÉ / INTERSECTION',
    accent: '#facc15',
    accentSoft: 'rgba(250,204,21,0.12)',
    description: 'Régit l\'ordre de passage à un croisement (priorité, cédez, stop, giratoire).',
  },
  interdiction: {
    label: 'INTERDICTION',
    accent: '#cf2031',
    accentSoft: 'rgba(207,32,49,0.10)',
    description: 'Disque blanc bordé de rouge. Pose une interdiction ou une limitation.',
  },
  obligation: {
    label: 'OBLIGATION',
    accent: '#1d4ed8',
    accentSoft: 'rgba(29,78,216,0.10)',
    description: 'Disque bleu plein. Impose une action obligatoire.',
  },
  indication: {
    label: 'INDICATION',
    accent: '#1d4ed8',
    accentSoft: 'rgba(29,78,216,0.10)',
    description: 'Carré bleu plein. Donne une information utile sans obligation.',
  },
  feu: {
    label: 'FEU TRICOLORE',
    accent: '#3f3f46',
    accentSoft: 'rgba(63,63,70,0.10)',
    description: 'Régulation des intersections : rouge = arrêt, orange = ralentir, vert = passer.',
  },
};

// ============================================================
// FORMES GÉOMÉTRIQUES OFFICIELLES (sans pictogramme intérieur)
// ============================================================
// On rend uniquement la silhouette officielle, sans dessin interne.
// Le code du panneau est inscrit au centre pour identification rapide.

function DangerShape({ codeDisplay }) {
  return (
    <svg viewBox="0 0 80 70" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <polygon
        points="40,4 76,66 4,66"
        fill="#fff"
        stroke="#cf2031"
        strokeWidth="6"
        strokeLinejoin="round"
      />
      <text x="40" y="55" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#1f2937" textAnchor="middle">
        {codeDisplay}
      </text>
    </svg>
  );
}

function CedezShape({ codeDisplay }) {
  return (
    <svg viewBox="0 0 80 70" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <polygon
        points="40,66 4,4 76,4"
        fill="#fff"
        stroke="#cf2031"
        strokeWidth="6"
        strokeLinejoin="round"
      />
      <text x="40" y="32" fontFamily="Inter, system-ui, sans-serif" fontSize="13" fontWeight="800" fill="#1f2937" textAnchor="middle">
        {codeDisplay}
      </text>
    </svg>
  );
}

function OctogoneStop() {
  return (
    <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <polygon
        points="24,4 56,4 76,24 76,56 56,76 24,76 4,56 4,24"
        fill="#cf2031"
        stroke="#fff"
        strokeWidth="3"
      />
      <text x="40" y="48" fontFamily="Arial Black, Inter, sans-serif" fontSize="22" fontWeight="900" fill="#fff" textAnchor="middle">
        STOP
      </text>
    </svg>
  );
}

function LosangePrioritaire() {
  return (
    <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <polygon points="40,4 76,40 40,76 4,40" fill="#fff" stroke="#000" strokeWidth="3" />
      <polygon points="40,14 66,40 40,66 14,40" fill="#facc15" stroke="#000" strokeWidth="2" />
    </svg>
  );
}

function InterdictionShape({ codeDisplay, isLimitation, speedValue }) {
  return (
    <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <circle cx="40" cy="40" r="36" fill="#fff" stroke="#cf2031" strokeWidth="7" />
      {isLimitation ? (
        <text
          x="40"
          y="52"
          fontFamily="Arial Black, Inter, sans-serif"
          fontSize={String(speedValue).length >= 3 ? '24' : '30'}
          fontWeight="900"
          fill="#1f2937"
          textAnchor="middle"
        >
          {speedValue}
        </text>
      ) : (
        <text x="40" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#1f2937" textAnchor="middle">
          {codeDisplay}
        </text>
      )}
    </svg>
  );
}

function ObligationShape({ codeDisplay }) {
  return (
    <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <circle cx="40" cy="40" r="38" fill="#1d4ed8" />
      <text x="40" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#fff" textAnchor="middle">
        {codeDisplay}
      </text>
    </svg>
  );
}

function IndicationShape({ codeDisplay }) {
  return (
    <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="2" y="2" width="76" height="76" rx="4" fill="#1d4ed8" stroke="#fff" strokeWidth="2" />
      <text x="40" y="48" fontFamily="Inter, system-ui, sans-serif" fontSize="14" fontWeight="800" fill="#fff" textAnchor="middle">
        {codeDisplay}
      </text>
    </svg>
  );
}

function FeuShape({ activeColor }) {
  return (
    <svg viewBox="0 0 60 100" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect x="6" y="6" width="48" height="88" rx="6" fill="#1f2937" />
      <circle cx="30" cy="24" r="14" fill={activeColor === 'rouge' ? '#cf2031' : '#3f3f46'} />
      <circle cx="30" cy="50" r="14" fill={activeColor === 'orange' ? '#f59e0b' : '#3f3f46'} />
      <circle cx="30" cy="76" r="14" fill={activeColor === 'vert' ? '#16a34a' : '#3f3f46'} />
    </svg>
  );
}

// ============================================================
// BIBLIOTHÈQUE DE PANNEAUX (référentiel ECSR 2026)
// ============================================================
// Chaque entrée :
//   { label: description courte, cat: catégorie }
// La FORME et la COULEUR sont déduites de la catégorie.
// Le RENDU visuel n'utilise PLUS de pictogrammes erronés — uniquement la
// silhouette officielle + le code centré.

const SIGNS = {
  // ---------- DANGER (A) ----------
  A1a: { label: 'Virage à gauche', cat: 'danger' },
  A1b: { label: 'Virage à droite', cat: 'danger' },
  A1c: { label: 'Doubles virages, premier à gauche', cat: 'danger' },
  A1d: { label: 'Succession de virages (3 ou plus)', cat: 'danger' },
  A2a: { label: "Cassis ou dos d'âne", cat: 'danger' },
  A2b: { label: "Ralentisseur de type dos d'âne", cat: 'danger' },
  A3: { label: 'Chaussée rétrécie', cat: 'danger' },
  A4: { label: 'Chaussée glissante', cat: 'danger' },
  A7: { label: 'Passage à niveau avec barrières', cat: 'danger' },
  A8: { label: 'Passage à niveau sans barrières', cat: 'danger' },
  A13a: { label: 'Endroit fréquenté par des enfants', cat: 'danger' },
  A13b: { label: 'Passage piéton', cat: 'danger' },
  A14: { label: 'Autres dangers', cat: 'danger' },
  A15a1: { label: "Passage d'animaux domestiques", cat: 'danger' },
  A15b: { label: "Passage d'animaux sauvages", cat: 'danger' },
  A15c: { label: 'Passage de cavaliers', cat: 'danger' },
  A19: { label: 'Chute de pierres', cat: 'danger' },
  A21: { label: 'Débouché de cyclistes', cat: 'danger' },
  A23: { label: "Traversée d'une voie de tramway", cat: 'danger' },
  A24: { label: 'Vent latéral', cat: 'danger' },

  // ---------- INTERSECTIONS / PRIORITÉ (AB) ----------
  AB1: { label: 'Priorité à droite (intersection)', cat: 'intersection' },
  AB2: { label: 'Route à caractère prioritaire', cat: 'intersection' },
  AB3a: { label: 'Cédez le passage', cat: 'intersection' },
  AB4: { label: 'Stop : marquer l\'arrêt absolu', cat: 'intersection' },
  AB6: { label: 'Carrefour de routes prioritaires', cat: 'intersection' },
  AB25: { label: 'Carrefour à sens giratoire', cat: 'intersection' },

  // ---------- INTERDICTION (B) ----------
  B1: { label: 'Sens interdit à tout véhicule', cat: 'interdiction' },
  B2a: { label: 'Interdiction de tourner à gauche', cat: 'interdiction' },
  B2b: { label: 'Interdiction de tourner à droite', cat: 'interdiction' },
  B3: { label: 'Dépassement interdit', cat: 'interdiction' },
  B6a1: { label: 'Stationnement interdit', cat: 'interdiction' },
  B14_30: { label: 'Limitation de vitesse 30 km/h', cat: 'interdiction', limitation: 30 },
  B14_50: { label: 'Limitation de vitesse 50 km/h', cat: 'interdiction', limitation: 50 },
  B14_70: { label: 'Limitation de vitesse 70 km/h', cat: 'interdiction', limitation: 70 },
  B14_80: { label: 'Limitation de vitesse 80 km/h', cat: 'interdiction', limitation: 80 },
  B14_90: { label: 'Limitation de vitesse 90 km/h', cat: 'interdiction', limitation: 90 },
  B14_110: { label: 'Limitation de vitesse 110 km/h', cat: 'interdiction', limitation: 110 },
  B14_130: { label: 'Limitation de vitesse 130 km/h', cat: 'interdiction', limitation: 130 },

  // ---------- OBLIGATION (rond bleu) ----------
  B21a1: { label: 'Obligation : aller tout droit', cat: 'obligation' },
  B22a: { label: 'Piste cyclable obligatoire', cat: 'obligation' },

  // ---------- INDICATION (carré bleu) ----------
  C12: { label: 'Sens unique', cat: 'indication' },
  C13a: { label: 'Impasse', cat: 'indication' },
  C20a: { label: 'Passage piéton (indication)', cat: 'indication' },
  C24a: { label: 'Voie réservée aux bus', cat: 'indication' },
  C107: { label: 'Entrée d\'autoroute', cat: 'indication' },
  C115: { label: 'Voie réservée aux deux-roues', cat: 'indication' },

  // ---------- FEUX TRICOLORES ----------
  FEU_ROUGE: { label: 'Feu rouge : arrêt absolu', cat: 'feu', feuColor: 'rouge' },
  FEU_ORANGE: { label: 'Feu orange : ralentir et s\'arrêter si possible', cat: 'feu', feuColor: 'orange' },
  FEU_VERT: { label: 'Feu vert : passage autorisé avec prudence', cat: 'feu', feuColor: 'vert' },
};

// Codes spéciaux qui remplacent la silhouette générique de leur catégorie
const SPECIAL_RENDERERS = {
  AB3a: 'cedez',
  AB4: 'octogone',
  AB2: 'losange',
  AB6: 'losange',
};

function renderShape(code, sign) {
  // Cas spéciaux d'abord (cédez, stop, losange prioritaire)
  const special = SPECIAL_RENDERERS[code];
  if (special === 'cedez') return <CedezShape codeDisplay={code} />;
  if (special === 'octogone') return <OctogoneStop />;
  if (special === 'losange') return <LosangePrioritaire />;

  // Sinon par catégorie
  switch (sign.cat) {
    case 'danger':
      return <DangerShape codeDisplay={code} />;
    case 'intersection':
      // Par défaut triangle (priorité à droite, etc.)
      return <DangerShape codeDisplay={code} />;
    case 'interdiction':
      return (
        <InterdictionShape
          codeDisplay={code}
          isLimitation={Boolean(sign.limitation)}
          speedValue={sign.limitation}
        />
      );
    case 'obligation':
      return <ObligationShape codeDisplay={code} />;
    case 'indication':
      return <IndicationShape codeDisplay={code} />;
    case 'feu':
      return <FeuShape activeColor={sign.feuColor} />;
    default:
      return null;
  }
}

// ============================================================
// API PUBLIQUE (compatible avec l'usage existant)
// ============================================================

export const ROAD_SIGN_CODES = Object.keys(SIGNS);

/**
 * Affiche un panneau routier sous forme de fiche d'identité (forme officielle
 * + couleur + code + label optionnel).
 *
 * @param {string} code   Ex : "A1a", "B14_50", "AB4", "FEU_ROUGE"
 * @param {number} size   Taille en px (défaut 96)
 * @param {boolean} label Si true, affiche le code + label sous la forme
 * @param {string} className Classes CSS additionnelles
 */
export function RoadSign({ code, size = 96, label = false, className }) {
  const sign = SIGNS[code];
  if (!sign) return null;
  const meta = CAT_META[sign.cat];
  return (
    <div
      data-testid={`road-sign-${code}`}
      className={cn('inline-flex flex-col items-center gap-1.5', className)}
      title={`${code} — ${sign.label} (${meta?.label || sign.cat})`}
    >
      <div style={{ width: size, height: code.startsWith('FEU_') ? size * 1.4 : size }} className="flex items-center justify-center">
        {renderShape(code, sign)}
      </div>
      {label && (
        <div className="text-[10px] text-muted-foreground text-center max-w-[140px] leading-tight">
          <span className="font-mono font-bold">{code}</span>
          <span className="block text-[9px] text-foreground/55 uppercase tracking-wide font-bold mt-0.5">
            {meta?.label}
          </span>
          <span className="block mt-0.5">{sign.label}</span>
        </div>
      )}
    </div>
  );
}

/** Renvoie le label texte d'un panneau (utilisé en lecture seule par d'autres composants). */
export function getRoadSignLabel(code) {
  return SIGNS[code]?.label || null;
}

/** Renvoie la catégorie d'un panneau (danger, interdiction, …). */
export function getRoadSignCategory(code) {
  return SIGNS[code]?.cat || null;
}

/** Renvoie les méta-données d'une catégorie (label, accent, description). */
export function getRoadSignCategoryMeta(cat) {
  return CAT_META[cat] || null;
}
